/**
 * GeoJSON utility — closed-ring polygon helpers for the geofence editor (DISPATCH-04 / GEO-02).
 *
 * Phase 9 / Plan 09-06 — pure functions that mirror the server-side Zod closed-ring
 * validation (Plan 09-02 geofence-put.ts). The client validates BEFORE calling PUT so
 * a dispatcher never fires an API call with a degenerate polygon (T-09-06-02 defense).
 *
 * Spec: 09-06-PLAN.md Task 1 / <interfaces> "closed-ring contract".
 */

/**
 * Ensure a coordinate ring is closed: append the first coord at the end if it
 * is not already equal to the last coord.
 *
 * @param coords  Array of [lng, lat] coordinate pairs.
 * @returns       A GeoJSON Polygon Feature with the ring closed.
 */
export function toClosedRingFeature(coords: [number, number][]): GeoJSON.Feature<GeoJSON.Polygon> {
  if (coords.length === 0) {
    return {
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [[]] },
      properties: {},
    };
  }

  const ring = [...coords];
  const first = ring[0];
  const last = ring[ring.length - 1];

  // Close the ring if first !== last
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([first[0], first[1]]);
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [ring],
    },
    properties: {},
  };
}

/**
 * Validate that a JSON string represents a closed-ring GeoJSON Polygon Feature.
 *
 * Mirrors the server Zod refine from Plan 09-02 geofence-put.ts:
 *   - Must parse as JSON
 *   - Must have geometry.type === 'Polygon'
 *   - Ring length >= 4 (at least 3 distinct points + closing repeat)
 *   - First coordinate must deep-equal last coordinate (closed ring)
 *
 * @returns true if valid closed ring, false otherwise.
 */
export function isClosedRingPolygon(featureJson: string): boolean {
  try {
    const f = JSON.parse(featureJson) as {
      geometry?: {
        type?: string;
        coordinates?: Array<Array<[number, number]>>;
      };
    };
    const ring = f?.geometry?.coordinates?.[0];
    if (!Array.isArray(ring) || ring.length < 4) return false;
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (!first || !last) return false;
    return first[0] === last[0] && first[1] === last[1];
  } catch {
    return false;
  }
}

/**
 * Serialize a GeoJSON Feature to a JSON string for the PUT request body.
 */
export function featureToString(feature: GeoJSON.Feature<GeoJSON.Polygon>): string {
  return JSON.stringify(feature);
}

/**
 * Extract the coordinate ring from a stored polygonGeojson string.
 * Used to load an existing polygon into mapbox-gl-draw via draw.add().
 *
 * @param polygonGeojson  Stored GeoJSON Feature string.
 * @returns               Array of [lng, lat] coordinate pairs (the outer ring).
 */
export function ringFromGeofence(polygonGeojson: string): [number, number][] {
  try {
    const f = JSON.parse(polygonGeojson) as {
      geometry?: {
        coordinates?: Array<[number, number][]>;
      };
    };
    const ring = f?.geometry?.coordinates?.[0];
    if (!Array.isArray(ring)) return [];
    return ring;
  } catch {
    return [];
  }
}
